import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { BellRing, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REMINDERS = [
  {
    key: "training_reminder",
    name: "Training attendance",
    what: "Asks parents to confirm their child is coming to training",
    when: (b) => {
      const days = (b.days_before || []).join(", ");
      return days
        ? `${days} day(s) before at ${b.send_time || "12:00"}`
        : `${(b.hours_before || []).join(", ")} hour(s) before`;
    },
  },
  {
    key: "league_game_reminder",
    name: "Game day",
    what: "Reminds parents about a game and asks YES or NO",
    when: (b) =>
      `${(b.default_days_before || []).join(", ")} day(s) before at ${b.send_time || "18:00"}`
      + ` · game morning at ${b.morning_time || "07:30"}`,
  },
  {
    key: "trial_reminder",
    name: "Trial reminder",
    what: "Nudges a booked trialist so they don't forget",
    when: () => "The day before the trial",
  },
  {
    key: "cancellation_notice",
    name: "Cancellation notice",
    what: "Tells families when rain or haze calls a session off",
    when: () => "Sent the moment you cancel",
  },
];

/** Every reminder in one place, each with its own on/off switch. */
export const RemindersOverview = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/whatsapp/settings`, { withCredentials: true });
      setSettings(r.data.settings || r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the reminders");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = async (key, name, on) => {
    setSaving(key);
    const block = { ...(settings[key] || {}), enabled: on };
    try {
      await axios.put(`${API}/admin/whatsapp/settings`, { [key]: block },
        { withCredentials: true });
      setSettings({ ...settings, [key]: block });
      toast.success(`${name} ${on ? "switched on" : "switched off"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that");
    } finally {
      setSaving("");
    }
  };

  if (!settings) return <p className="text-sm text-slate-400">Loading…</p>;

  const masterOff = !settings.enabled;

  return (
    <Card className="p-6 bg-slate-900/50" data-testid="reminders-overview-card">
      <div className="flex items-start gap-2">
        <BellRing className="w-5 h-5 text-amber-400 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-white">Reminders</h3>
          <p className="text-sm text-slate-400 mt-1">
            Every automatic WhatsApp, with its own switch. Turn one off and the rest carry on.
          </p>
        </div>
      </div>

      {masterOff && (
        <p className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
          data-testid="reminders-master-off">
          WhatsApp is switched off altogether in <strong>Credentials</strong>, so nothing below
          will send until you turn it back on.
        </p>
      )}

      <div className="mt-5 space-y-2">
        {REMINDERS.map((r) => {
          const block = settings[r.key] || {};
          const on = block.enabled !== false && !!block.enabled;
          return (
            <div key={r.key}
              className="flex items-start justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3"
              data-testid={`reminder-row-${r.key}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-white">{r.name}</span>
                  <Badge className={`text-[10px] ${on
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "bg-slate-700/50 text-slate-400"}`}
                    data-testid={`reminder-state-${r.key}`}>
                    {on ? "on" : "off"}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 mt-1">{r.what}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{r.when(block)}</p>
              </div>
              <div className="flex items-center gap-2">
                {saving === r.key && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                <Switch checked={on} disabled={saving === r.key}
                  onCheckedChange={(v) => toggle(r.key, r.name, v)}
                  data-testid={`reminder-toggle-${r.key}`} />
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-500 mt-4">
        Wording and timings live in each reminder&apos;s own card further down this page.
      </p>
    </Card>
  );
};
